import { usePathname } from 'next/navigation';
import { FC } from 'react';
import { Scrollbar } from 'src/common/components/Scrollbar';
import { useSettings } from 'src/common/hooks/useSettings';
import type { NavColor } from 'src/common/types/settings';
import ProjectSelector from 'src/modules/projects/components/common/project-selector';

import { HomeButton } from '@/components/buttons/HomeButton';
import { cn } from '@/design-system/lib/utils';

import { LayoutType } from '../../../common/types/layout';
import type { Section } from '../config';
import { ProductRail } from './product-rail';
import { SideNavSection } from './side-nav-section';

const SIDE_NAV_WIDTH = 280;

const getCssVars = (color: NavColor, isDark: boolean): Record<string, string> => {
  switch (color) {
    case 'blend-in':
      if (isDark) {
        return {
          '--nav-bg': '#0B0F19',
          '--nav-color': '#F3F4F6',
          '--nav-border-color': '#2F3746',
          '--nav-logo-border': '#2F3746',
          '--nav-section-title-color': '#9DA4AE',
          '--nav-item-color': '#9DA4AE',
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': '#EDF2F7',
          '--nav-item-disabled-color': '#4D5761',
          '--nav-item-icon-color': '#6C737F',
          '--nav-item-icon-active-color': '#4C6EF5',
          '--nav-item-icon-disabled-color': '#2F3746',
          '--nav-item-chevron-color': '#2F3746',
          '--nav-scrollbar-color': '#9DA4AE',
        };
      }
      return {
        '--nav-bg': '#F8F9FA',
        '--nav-color': '#111927',
        '--nav-border-color': '#F3F4F6',
        '--nav-logo-border': '#F3F4F6',
        '--nav-section-title-color': '#9DA4AE',
        '--nav-item-color': '#6C737F',
        '--nav-item-hover-bg': 'rgba(17, 25, 39, 0.04)',
        '--nav-item-active-bg': 'rgba(17, 25, 39, 0.12)',
        '--nav-item-active-color': '#111927',
        '--nav-item-disabled-color': '#9DA4AE',
        '--nav-item-icon-color': '#9DA4AE',
        '--nav-item-icon-active-color': '#4C6EF5',
        '--nav-item-icon-disabled-color': '#9DA4AE',
        '--nav-item-chevron-color': '#9DA4AE',
        '--nav-scrollbar-color': '#111927',
      };

    case 'discreet':
      if (isDark) {
        return {
          '--nav-bg': '#111927',
          '--nav-color': '#F3F4F6',
          '--nav-border-color': '#2F3746',
          '--nav-logo-border': '#2F3746',
          '--nav-section-title-color': '#9DA4AE',
          '--nav-item-color': '#9DA4AE',
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': '#EDF2F7',
          '--nav-item-disabled-color': '#4D5761',
          '--nav-item-icon-color': '#6C737F',
          '--nav-item-icon-active-color': '#4C6EF5',
          '--nav-item-icon-disabled-color': '#2F3746',
          '--nav-item-chevron-color': '#2F3746',
          '--nav-scrollbar-color': '#9DA4AE',
        };
      }
      return {
        '--nav-bg': '#F8F9FA',
        '--nav-color': '#111927',
        '--nav-border-color': 'rgb(228, 228, 231)',
        '--nav-logo-border': '#E5E7EB',
        '--nav-section-title-color': '#6C737F',
        '--nav-item-color': '#6C737F',
        '--nav-item-hover-bg': 'rgba(17, 25, 39, 0.04)',
        '--nav-item-active-bg': 'rgba(17, 25, 39, 0.12)',
        '--nav-item-active-color': '#111927',
        '--nav-item-disabled-color': '#9DA4AE',
        '--nav-item-icon-color': '#9DA4AE',
        '--nav-item-icon-active-color': '#4C6EF5',
        '--nav-item-icon-disabled-color': '#9DA4AE',
        '--nav-item-chevron-color': '#9DA4AE',
        '--nav-scrollbar-color': '#111927',
      };

    case 'evident':
    default:
      if (isDark) {
        return {
          '--nav-bg': '#1C2536',
          '--nav-color': '#ffffff',
          '--nav-border-color': 'transparent',
          '--nav-logo-border': '#2F3746',
          '--nav-section-title-color': '#9DA4AE',
          '--nav-item-color': '#9DA4AE',
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': '#ffffff',
          '--nav-item-disabled-color': '#6C737F',
          '--nav-item-icon-color': '#9DA4AE',
          '--nav-item-icon-active-color': '#4C6EF5',
          '--nav-item-icon-disabled-color': '#6C737F',
          '--nav-item-chevron-color': '#4D5761',
          '--nav-scrollbar-color': '#9DA4AE',
        };
      }
      return {
        '--nav-bg': '#1C2536',
        '--nav-color': '#ffffff',
        '--nav-border-color': 'transparent',
        '--nav-logo-border': '#2F3746',
        '--nav-section-title-color': '#9DA4AE',
        '--nav-item-color': '#9DA4AE',
        '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
        '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
        '--nav-item-active-color': '#ffffff',
        '--nav-item-disabled-color': '#6C737F',
        '--nav-item-icon-color': '#9DA4AE',
        '--nav-item-icon-active-color': '#4C6EF5',
        '--nav-item-icon-disabled-color': '#6C737F',
        '--nav-item-chevron-color': '#4D5761',
        '--nav-scrollbar-color': '#9DA4AE',
      };
  }
};

interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
  layoutType?: LayoutType;
}

export const SideNav: FC<SideNavProps> = (props) => {
  const { color = 'evident', sections = [] } = props;
  const pathname = usePathname();
  const settings = useSettings();
  const isDark = settings.paletteMode === 'dark';
  const cssVars = getCssVars(color, isDark);

  return (
    <div
      className="tw:fixed tw:top-0 tw:left-0 tw:bottom-0 tw:z-[1200] tw:border-r"
      style={{
        ...(cssVars as React.CSSProperties),
        backgroundColor: 'var(--nav-bg)',
        borderRightColor: 'var(--nav-border-color)',
        color: 'var(--nav-color)',
        width: SIDE_NAV_WIDTH,
      }}
    >
      <div className="tw:flex tw:flex-row tw:h-full">
        {props.layoutType === LayoutType.PROJECT && <ProductRail />}
        <Scrollbar style={{ height: '100%', overflow: 'hidden', flex: 1 }}>
          <div className="tw:flex tw:flex-col tw:h-full tw:overflow-hidden">
            {props.layoutType !== LayoutType.PROJECT && (
              <div className="tw:flex tw:flex-row tw:items-center tw:p-6 tw:gap-4">
                <HomeButton />
              </div>
            )}
            {/* Project navs open straight on their first group, so the panel needs
                the top breathing room the HomeButton block used to provide. */}
            <nav
              className={cn(
                'tw:flex tw:flex-col tw:gap-4 tw:flex-1 tw:px-2 tw:overflow-auto',
                props.layoutType === LayoutType.PROJECT && 'tw:pt-5',
              )}
            >
              {sections.map((section, index) => (
                <SideNavSection items={section.items} key={index} pathname={pathname} subheader={section.subheader} />
              ))}
            </nav>
            {props.layoutType === LayoutType.PROJECT && (
              <div className="tw:px-2 tw:py-6 tw:flex tw:flex-col tw:gap-8">
                <ProjectSelector />
              </div>
            )}
          </div>
        </Scrollbar>
      </div>
    </div>
  );
};
